// src/components/PhotoGalleryModal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import "../scss/components/_photogallerymodal.scss";

const TAB_ORDER = ["PG", "SINGLE", "DOUBLE", "TRIPLE", "QUAD", "DORM"];

const formatTypeName = (type) => {
  if (type === "PG") return "Common";
  if (type === "SINGLE") return "Single Sharing";
  if (type === "DOUBLE") return "Double Sharing";
  if (type === "TRIPLE") return "Triple Sharing";
  if (type === "QUAD") return "Quad Sharing";
  if (type === "DORM") return "Dorm Sharing";
  return type;
};

const typeBadgeLetter = (type) => {
  if (type === "SINGLE") return "S";
  if (type === "DOUBLE") return "D";
  if (type === "TRIPLE") return "T";
  if (type === "QUAD") return "Q";
  if (type === "DORM") return "Do";
  return null;
};

const PhotoGalleryModal = ({ photos = [], onClose, initialTab = "All" }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "lightbox"
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Lightbox interaction state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const overlayRef = useRef(null);
  const imageContainerRef = useRef(null);
  const thumbnailContainerRef = useRef(null);
  const gridScrollRef = useRef(null);

  // ── Grouping / filtering ─────────────────────────────────────────
  const groupedPhotos = React.useMemo(() => {
    const groups = {};
    photos.forEach((p) => {
      const t = p.PhotoType || "Other";
      (groups[t] ||= []).push(p);
    });
    return groups;
  }, [photos]);

  const photoTypes = React.useMemo(() => {
    const types = ["All"];
    TAB_ORDER.forEach((t) => groupedPhotos[t] && types.push(t));
    Object.keys(groupedPhotos).forEach((t) => !types.includes(t) && types.push(t));
    return types;
  }, [groupedPhotos]);

  const filteredPhotos =
    activeTab === "All" ? photos : groupedPhotos[activeTab] || [];

  const getTypeCount = (type) =>
    type === "All" ? photos.length : groupedPhotos[type]?.length || 0;

  // ── Effects ──────────────────────────────────────────────────────
  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Reset viewer state when the filter changes
  useEffect(() => {
    setSelectedIndex(0);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    if (gridScrollRef.current) gridScrollRef.current.scrollTop = 0;
  }, [activeTab]);

  // Keyboard: works in both modes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (viewMode === "lightbox") setViewMode("grid");
        else onClose();
        return;
      }
      if (viewMode !== "lightbox") return;
      if (e.key === "ArrowRight") handleNext();
      else if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "+" || e.key === "=") handleZoomIn();
      else if (e.key === "-") handleZoomOut();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, filteredPhotos.length, selectedIndex]);

  // Scroll active thumbnail into view in lightbox
  useEffect(() => {
    if (viewMode !== "lightbox") return;
    const el = thumbnailContainerRef.current?.querySelectorAll(".photo-thumb-item")[selectedIndex];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [selectedIndex, viewMode]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const openLightbox = (index) => {
    setSelectedIndex(index);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    setViewMode("lightbox");
  };

  const handlePrev = useCallback(() => {
    if (!filteredPhotos.length) return;
    setSelectedIndex((i) => (i - 1 + filteredPhotos.length) % filteredPhotos.length);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [filteredPhotos.length]);

  const handleNext = useCallback(() => {
    if (!filteredPhotos.length) return;
    setSelectedIndex((i) => (i + 1) % filteredPhotos.length);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [filteredPhotos.length]);

  const handleZoomIn = () => setZoomLevel((p) => Math.min(p + 0.25, 3));
  const handleZoomOut = () => {
    setZoomLevel((p) => {
      const next = Math.max(p - 0.25, 0.5);
      if (next <= 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };
  const handleZoomReset = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const handleMouseMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (zoomLevel <= 1) return;
    const t = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: t.clientX - position.x, y: t.clientY - position.y });
  };
  const handleTouchMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    const t = e.touches[0];
    setPosition({ x: t.clientX - dragStart.x, y: t.clientY - dragStart.y });
  };
  const handleTouchEnd = () => setIsDragging(false);

  const currentPhoto = filteredPhotos[selectedIndex] || null;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div
      className={`photo-gallery-overlay mode-${viewMode}`}
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div className="photo-gallery-modal">
        {/* Sticky header — filters + close */}
        <div className="photo-gallery-header">
          <div className="photo-gallery-tabs" role="tablist">
            {photoTypes.map((type) => (
              <button
                key={type}
                role="tab"
                aria-selected={activeTab === type}
                className={`photo-tab ${activeTab === type ? "active" : ""}`}
                onClick={() => setActiveTab(type)}
              >
                <span>{type === "All" ? "All Photos" : formatTypeName(type)}</span>
                <span className="photo-tab-count">{getTypeCount(type)}</span>
              </button>
            ))}
          </div>

          <div className="photo-gallery-header-actions">
            {viewMode === "lightbox" && (
              <button
                className="photo-gallery-icon-btn"
                onClick={() => setViewMode("grid")}
                aria-label="Back to grid"
                title="Back to grid"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
              </button>
            )}
            <button className="photo-gallery-icon-btn photo-gallery-close" onClick={onClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M2 2L18 18M18 2L2 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        {viewMode === "grid" ? (
          <div className="photo-grid-scroll" ref={gridScrollRef}>
            {filteredPhotos.length === 0 ? (
              <div className="photo-grid-empty">
                <p>No photos in this category</p>
              </div>
            ) : (
              <div className="photo-masonry">
                {filteredPhotos.map((photo, index) => {
                  const badge = typeBadgeLetter(photo.PhotoType);
                  return (
                    <button
                      key={photo.PhotoID || index}
                      type="button"
                      className="masonry-item"
                      onClick={() => openLightbox(index)}
                      aria-label={photo.PhotoTitle || `Open photo ${index + 1}`}
                    >
                      <img
                        src={photo.PhotoURL}
                        alt={photo.PhotoTitle || `Photo ${index + 1}`}
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="masonry-item-overlay">
                        <span className="masonry-item-type">
                          {formatTypeName(photo.PhotoType || "PG")}
                        </span>
                        <span className="masonry-item-expand" aria-hidden>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9" />
                            <polyline points="9 21 3 21 3 15" />
                            <line x1="21" y1="3" x2="14" y2="10" />
                            <line x1="3" y1="21" x2="10" y2="14" />
                          </svg>
                        </span>
                      </span>
                      {badge && <span className="masonry-item-badge">{badge}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="photo-lightbox">
            <div
              className="photo-main-area"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              ref={imageContainerRef}
            >
              {currentPhoto ? (
                <>
                  <div className="photo-image-info">
                    {currentPhoto.PhotoType && (
                      <span className="photo-image-type">
                        {formatTypeName(currentPhoto.PhotoType)}
                      </span>
                    )}
                  </div>

                  <button className="photo-nav-btn photo-nav-prev" onClick={handlePrev} aria-label="Previous">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  <div
                    className="photo-main-wrapper"
                    style={{
                      transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
                      cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                      transition: isDragging ? "none" : "transform 0.2s ease",
                    }}
                  >
                    <img
                      src={currentPhoto.PhotoURL}
                      alt={currentPhoto.PhotoTitle || "Photo"}
                      className="photo-main-image"
                      draggable={false}
                    />
                  </div>

                  <button className="photo-nav-btn photo-nav-next" onClick={handleNext} aria-label="Next">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  <div className="photo-counter">
                    {selectedIndex + 1} / {filteredPhotos.length}
                  </div>

                  <div className="photo-controls">
                    <button className="photo-control-btn" onClick={handleZoomOut} title="Zoom out" aria-label="Zoom out">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        <line x1="8" y1="11" x2="14" y2="11" />
                      </svg>
                    </button>
                    <button className="photo-control-btn" onClick={handleZoomReset} title="Reset" aria-label="Reset zoom">
                      <span style={{ fontSize: 11, fontWeight: 600 }}>
                        {Math.round(zoomLevel * 100)}%
                      </span>
                    </button>
                    <button className="photo-control-btn" onClick={handleZoomIn} title="Zoom in" aria-label="Zoom in">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        <line x1="11" y1="8" x2="11" y2="14" />
                        <line x1="8" y1="11" x2="14" y2="11" />
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                <div className="photo-main-empty">
                  <p>No photos available</p>
                </div>
              )}
            </div>

            <div className="photo-thumbnails-container" ref={thumbnailContainerRef}>
              <div className="photo-thumbnails">
                {filteredPhotos.map((photo, index) => (
                  <button
                    key={photo.PhotoID || index}
                    type="button"
                    className={`photo-thumb-item ${selectedIndex === index ? "active" : ""}`}
                    onClick={() => {
                      setSelectedIndex(index);
                      setZoomLevel(1);
                      setPosition({ x: 0, y: 0 });
                    }}
                    aria-label={`View photo ${index + 1}`}
                  >
                    <img src={photo.PhotoURL} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoGalleryModal;
